import { useMemo, useContext } from 'react'
import moment from 'moment'
import useAllCharterFlightJourneys from 'hooks/CharterFlights/useAllCharterFlightJourneys'
import { getFlightFareFamilies } from 'api/flights'
import { fareCabinLabelMap } from 'constants/flight'
import { mergeOccupants } from 'lib/offer/occupancyUtils'
import { useQuery } from '@tanstack/react-query'
import { EmptyObject } from 'lib/object/objectUtils'
import GeoContext from 'contexts/geoContext'
import getObjectKey from 'lib/object/getObjectKey'
import { UseBundleFlightDataParams, BundleFlightData, FlightDataByDate } from './types'
import { getSeatsLeftFromJourney } from './utils'

/**
 * Custom hook to handle flight-only data using charter flight journeys
 *
 * This hook handles:
 * - Fetching all charter flight journeys using useAllCharterFlightJourneys
 * - Matching journeys for each date range based on checkIn/checkOut dates
 * - Calculating flight prices using fare families (same as useCharterFlightPrice)
 * - Managing seatsLeft from journey data
 * - Bundle calculation is done in the parent hook
 *
 * @param params - Configuration parameters including dateRangesByMonth and charterBundleRoute
 * @returns Flight data by date
 */
export function useBundleFlightData(params: UseBundleFlightDataParams): BundleFlightData {
  const {
    rooms,
    dateRangesByMonth,
    fareClass,
    charterBundleRoute,
  } = params

  const { currentRegionCode } = useContext(GeoContext)

  // Get all charter flight journeys (same as useCharterFlightJourneysForHotelDates)
  const flightResults = useAllCharterFlightJourneys({
    charterBundleRoute,
    fareClass,
    occupancies: rooms,
    disabled: !charterBundleRoute,
  })

  const providerSearchId = flightResults?.searchId
  const fetchingJourneys = flightResults?.fetching
  const journeysError = flightResults?.error
  const searchResultsTimestamp = flightResults?.timestamp

  // Serialize rooms and dates to prevent infinite loops from array reference changes
  const roomsKey = useMemo(() => getObjectKey({ rooms }), [rooms])

  // Extract all date ranges for fare families queries
  const allDateRanges = useMemo(() => {
    return Object.values(dateRangesByMonth).flat()
  }, [dateRangesByMonth])

  // Map flight search results - match journeys for each date range
  const flightDataByDate = useMemo(() => {
    const dataMap: Record<string, FlightDataByDate> = {}

    if (!flightResults?.fares) {
      // If no flight data yet, mark all as fetching
      allDateRanges.forEach(({ checkIn }) => {
        dataMap[checkIn] = {
          fetching: true,
        }
      })
      return dataMap
    }

    const departingFares = flightResults.fares[0] ?? []
    const returningFares = flightResults.fares[1] ?? []

    allDateRanges.forEach(({ checkIn, checkOut }) => {
      // Match departing journey by checkIn date (arrival date)
      const departingJourney = checkIn ? departingFares.find(journey => {
        const arrivalDate = journey.flightGroup.flights.at(-1)?.arrivalDate
        return arrivalDate && moment(arrivalDate).format('YYYY-MM-DD') === checkIn
      }) : undefined

      // Match returning journey by checkOut date (departure date)
      const returningJourney = checkOut ? returningFares.find(journey => {
        const departingDate = journey.flightGroup.flights.at(0)?.departingDate
        return departingDate && moment(departingDate).format('YYYY-MM-DD') === checkOut
      }) : undefined

      if (departingJourney && returningJourney) {
        // Calculate seatsLeft from both journeys
        const departingSeatsLeft = getSeatsLeftFromJourney(departingJourney)
        const returningSeatsLeft = getSeatsLeftFromJourney(returningJourney)
        const seatsLeft = departingSeatsLeft !== undefined && returningSeatsLeft !== undefined
          ? Math.min(departingSeatsLeft, returningSeatsLeft)
          : departingSeatsLeft ?? returningSeatsLeft

        // Use journey prices as fallback (will be overridden by fare families if available)
        const departingFare = departingJourney.price?.all?.totalFare ?? 0
        const returningFare = returningJourney.price?.all?.totalFare ?? 0
        const totalFare = departingFare + returningFare

        dataMap[checkIn] = {
          fetching: false,
          totalPrice: totalFare,
          journey: departingJourney,
          seatsLeft,
        }
      } else {
        // No matching journeys found, check if search is complete
        const hasSearchCompleted = !!searchResultsTimestamp || !!journeysError
        dataMap[checkIn] = {
          fetching: !hasSearchCompleted,
        }
      }
    })

    return dataMap
  }, [allDateRanges, flightResults, searchResultsTimestamp, journeysError])

  // Fetch fare families for all date ranges that have matching journeys
  const fareFamiliesQueries = useMemo(() => {
    type ValidQuery = {
      checkIn: string
      departingJourney: App.JourneyV2
      returningJourney: App.JourneyV2
    }

    const queries: Array<ValidQuery> = []

    allDateRanges.forEach(({ checkIn, checkOut }) => {
      const departingJourney = checkIn && flightResults?.fares?.[0]?.find(journey => {
        const arrivalDate = journey.flightGroup.flights.at(-1)?.arrivalDate
        return arrivalDate && moment(arrivalDate).format('YYYY-MM-DD') === checkIn
      })
      const returningJourney = checkOut && flightResults?.fares?.[1]?.find(journey => {
        const departingDate = journey.flightGroup.flights.at(0)?.departingDate
        return departingDate && moment(departingDate).format('YYYY-MM-DD') === checkOut
      })

      if (departingJourney && returningJourney) {
        queries.push({
          checkIn,
          departingJourney,
          returningJourney,
        })
      }
    })

    return queries
  }, [allDateRanges, flightResults])

  // Use a single query to fetch fare families for all date ranges
  // This is more efficient than multiple queries
  const { data: fareFamiliesData, status: fareFamiliesStatus } = useQuery({
    queryFn: async() => {
      if (!providerSearchId || fareFamiliesQueries.length === 0) {
        return EmptyObject
      }

      // Fetch fare families for all date ranges in parallel
      const fareFamiliesPromises = fareFamiliesQueries.map(async({ checkIn, departingJourney, returningJourney }) => {
        try {
          const fareFamilies = await getFlightFareFamilies({
            searchId: providerSearchId,
            sessionId: '',
            origin: departingJourney.flightGroup.flights[0].departureAirport,
            destination: returningJourney.flightGroup.flights[0].departureAirport,
            occupancies: mergeOccupants(rooms),
            region: currentRegionCode,
            isStandalone: false,
            selectedFareIds: [departingJourney.id, returningJourney.id],
          })

          const fareCabinLabel = fareCabinLabelMap[fareClass]
          const departingFareFamily = fareFamilies.upsellFaresPerLeg?.[0]?.find(upsell => upsell.cabin === fareCabinLabel)
          const returningFareFamily = fareFamilies.upsellFaresPerLeg?.[1]?.find(upsell => upsell.cabin === fareCabinLabel)

          return {
            checkIn,
            departingJourney,
            returningJourney,
            fareFamilies,
            departingFareFamily,
            returningFareFamily,
          }
        } catch(_error) {
          return { checkIn, departingJourney, returningJourney, fareFamilies: undefined }
        }
      })

      const results = await Promise.allSettled(fareFamiliesPromises)
      return results.reduce((acc, settledResult) => {
        if (settledResult.status === 'fulfilled' && settledResult.value.checkIn) {
          acc[settledResult.value.checkIn] = settledResult.value
        }
        return acc
      }, {} as Record<string, { checkIn: string; departingJourney?: App.JourneyV2; returningJourney?: App.JourneyV2; fareFamilies?: any; departingFareFamily?: any; returningFareFamily?: any }>)
    },
    queryKey: ['charterFlightFareFamilies', providerSearchId, fareFamiliesQueries.map(q => `${q.checkIn}-${q.departingJourney.id}-${q.returningJourney.id}`).join(','), roomsKey, currentRegionCode],
    enabled: !!(providerSearchId && fareFamiliesQueries.length > 0),
  })

  // Update flight data with fare families prices
  const flightDataByDateWithFareFamilies = useMemo(() => {
    const dataMap = { ...flightDataByDate }

    if (fareFamiliesData) {
      Object.keys(fareFamiliesData).forEach(checkIn => {
        const fareFamilyData = fareFamiliesData[checkIn]
        if (fareFamilyData?.departingFareFamily && fareFamilyData?.returningFareFamily) {
          const totalFare = fareFamilyData.departingFareFamily.cost.all + fareFamilyData.returningFareFamily.cost.all
          if (dataMap[checkIn]) {
            dataMap[checkIn] = {
              ...dataMap[checkIn],
              totalPrice: totalFare,
            }
          }
        }
      })
    }

    // Update fetching status based on fare families query status
    if (fareFamiliesStatus === 'loading') {
      Object.keys(dataMap).forEach(checkIn => {
        if (dataMap[checkIn] && fareFamiliesQueries.some(q => q.checkIn === checkIn)) {
          dataMap[checkIn] = {
            ...dataMap[checkIn],
            fetching: true,
          }
        }
      })
    }

    return dataMap
  }, [flightDataByDate, fareFamiliesData, fareFamiliesStatus, fareFamiliesQueries])

  // Determine overall fetching status
  const isFetching = fetchingJourneys || fareFamiliesStatus === 'loading'

  // Update fetching status for all dates
  const finalFlightDataByDate = useMemo(() => {
    const dataMap = { ...flightDataByDateWithFareFamilies }
    if (isFetching) {
      Object.keys(dataMap).forEach(checkIn => {
        dataMap[checkIn] = {
          ...dataMap[checkIn],
          fetching: true,
        }
      })
    }
    return dataMap
  }, [flightDataByDateWithFareFamilies, isFetching])

  return {
    flightDataByDate: finalFlightDataByDate,
  }
}
